import { useState } from "react";
import { Money } from "@shop-ledger/shared-types";
import { Button, DataTable, type DataTableColumn } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { exportAccountingCsv, getPnl } from "../../api/reportsApi";
import type { PnlLineOut } from "../../api/types";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";

const TENANT_CURRENCY_FALLBACK = "UGX"; // Tenant.currency isn't returned by GET /auth/me
// (UserOut, `@shop-ledger/shared-types`'s api/auth.ts) — same documented stand-in every
// other money-displaying page in this app uses (see e.g. DashboardPage.tsx).

const COLUMNS: DataTableColumn<PnlLineOut, keyof PnlLineOut>[] = [
  { key: "account_code", header: "Code", render: (l) => l.account_code },
  { key: "account_name", header: "Account", render: (l) => l.account_name },
  { key: "total", header: "Amount", align: "right", render: (l) => Money.format(l.total, TENANT_CURRENCY_FALLBACK) },
];

/**
 * §5's "Dynamic Profit & Loss statement" — now **period-scoped**, reading the real
 * `GET /reports/pnl?date_from=&date_to=` (§10.5), which sums `TransactionLeg`
 * debits/credits per account with the correct normal-balance sign convention for
 * REVENUE/EXPENSE. Replaces the previous client-side all-time-only aggregation
 * (`domain/pnl.ts`), which existed only because `LedgerEntryOut` had no timestamp before
 * this backend pass.
 */
export function ProfitAndLossPage() {
  const { ensureFreshToken } = useAuth();
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const state = useAsyncData(
    () =>
      ensureFreshToken().then((token) =>
        getPnl(token, {
          dateFrom: dateFrom ? `${dateFrom}T00:00:00` : undefined,
          dateTo: dateTo ? `${dateTo}T23:59:59` : undefined,
        }),
      ),
    [dateFrom, dateTo],
  );

  const pnl = state.data;

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  async function handleExportAccounting() {
    setExportError(null);
    setExporting(true);
    try {
      const token = await ensureFreshToken();
      const blob = await exportAccountingCsv(token, {
        dateFrom: dateFrom ? `${dateFrom}T00:00:00` : undefined,
        dateTo: dateTo ? `${dateTo}T23:59:59` : undefined,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "accounting_export.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setExportError(error instanceof Error ? error.message : "Accounting export failed.");
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Profit &amp; Loss</h1>
          <p className="page__subtitle">Period-scoped revenue/expense totals. Leave both dates blank for all-time.</p>
        </div>
        <Button type="button" variant="secondary" onClick={() => void handleExportAccounting()} disabled={exporting}>
          {exporting ? "Exporting…" : "Accounting export (GL CSV)"}
        </Button>
      </div>

      {exportError ? <StatusBanner kind="error">{exportError}</StatusBanner> : null}
      {state.error ? <StatusBanner kind="error">{state.error}</StatusBanner> : null}

      <div className="filter-bar">
        <div className="sl-field">
          <label className="sl-field__label" htmlFor="pnl-from">
            From
          </label>
          <input id="pnl-from" className="sl-input" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div className="sl-field">
          <label className="sl-field__label" htmlFor="pnl-to">
            To
          </label>
          <input id="pnl-to" className="sl-input" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
      </div>

      {state.loading || !pnl ? (
        <p className="muted">Loading…</p>
      ) : (
        <>
          <div className="stat-row">
            <div className="stat-tile">
              <p className="stat-tile__label">Total revenue</p>
              <p className="stat-tile__value stat-tile__value--positive">{Money.format(pnl.revenue_total, TENANT_CURRENCY_FALLBACK)}</p>
            </div>
            <div className="stat-tile">
              <p className="stat-tile__label">Total expense</p>
              <p className="stat-tile__value stat-tile__value--negative">{Money.format(pnl.expense_total, TENANT_CURRENCY_FALLBACK)}</p>
            </div>
            <div className="stat-tile">
              <p className="stat-tile__label">Net income</p>
              <p className={`stat-tile__value ${pnl.net_income.startsWith("-") ? "stat-tile__value--negative" : "stat-tile__value--positive"}`}>
                {Money.format(pnl.net_income, TENANT_CURRENCY_FALLBACK)}
              </p>
            </div>
          </div>

          <div className="panel">
            <h2 className="panel__title">Revenue</h2>
            <DataTable columns={COLUMNS} rows={pnl.revenue_lines} getRowId={(row) => row.account_code} emptyMessage="No revenue posted in this period." />
          </div>

          <div className="panel">
            <h2 className="panel__title">Expenses</h2>
            <DataTable columns={COLUMNS} rows={pnl.expense_lines} getRowId={(row) => row.account_code} emptyMessage="No expenses posted in this period." />
          </div>
        </>
      )}
    </div>
  );
}
